import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import { CalendarDays, Check, ChevronRight, Clock3, Download, FileText, Filter, History, KeyRound, Leaf, Lock, LogOut, Search, Settings, ShieldAlert, UserCog, Utensils, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOG_PASSWORD = process.env.REACT_APP_LOG_PASSWORD || "gtpl@2026";

const DEFAULT_MEALS = [
  { name: "Breakfast", tone: "sunrise", windowDefault: { start_h: 7, start_m: 30, end_h: 10, end_m: 0 } },
  { name: "Lunch", tone: "leaf", windowDefault: { start_h: 12, start_m: 30, end_h: 15, end_m: 30 } },
  { name: "Evening Snacks", tone: "amber", windowDefault: { start_h: 16, start_m: 30, end_h: 18, end_m: 30 } },
  { name: "Dinner", tone: "night", windowDefault: { start_h: 19, start_m: 30, end_h: 22, end_m: 0 } },
];
const MEALS_WITH_PREFERENCE = new Set(["Lunch", "Dinner"]);
const WEEKDAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const windowToLabel = (w) => `${pad2(w.start_h)}:${pad2(w.start_m)} — ${pad2(w.end_h)}:${pad2(w.end_m)}`;
const windowToTimeString = (w, which) => `${pad2(w[`${which}_h`])}:${pad2(w[`${which}_m`])}`;
const parseTimeString = (s) => { const [h, m] = s.split(":").map(Number); return { h: h || 0, m: m || 0 }; };

const today = new Date();
const monthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
const monthStartISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const prettyDate = today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

function formatServiceDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function CheckIn({ onOpenLog, onOpenAdmin }) {
  const [step, setStep] = useState("form"); // form | otp | result
  const [form, setForm] = useState({ name: "", mobile: "", employee_code: "" });
  const [session, setSession] = useState(null); // { session_id, otp_code, meal_type, service_date, is_beyond_hours, allowed_preferences }
  const [otpInput, setOtpInput] = useState("");
  const [preference, setPreference] = useState(""); // "Veg" | "Non-Veg" | ""
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [publicSettings, setPublicSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => setPublicSettings(r.data)).catch(() => setPublicSettings(null));
  }, []);

  const mealPills = useMemo(() => {
    const windows = publicSettings?.meal_windows || {};
    return DEFAULT_MEALS.map((m) => ({
      ...m,
      time: windowToLabel(windows[m.name] || m.windowDefault),
    }));
  }, [publicSettings]);

  const resetAll = () => {
    setStep("form");
    setForm({ name: "", mobile: "", employee_code: "" });
    setSession(null);
    setOtpInput("");
    setPreference("");
    setResult(null);
    setError("");
  };

  const startCheckin = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/checkins/start`, form);
      setSession(response.data);
      setOtpInput("");
      // If only one preference allowed, auto-select it
      const allowed = response.data.allowed_preferences || [];
      setPreference(allowed.length === 1 ? allowed[0] : "");
      setStep("otp");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const allowedPrefs = session?.allowed_preferences || [];
  const needsPreference = session && MEALS_WITH_PREFERENCE.has(session.meal_type) && allowedPrefs.length > 0;
  const showChoice = needsPreference && allowedPrefs.length > 1;

  const confirmOtp = async (event) => {
    event.preventDefault();
    setError("");
    if (needsPreference && !preference) {
      setError(`Please choose Veg or Non-Veg for ${session.meal_type}.`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/checkins/confirm`, {
        session_id: session.session_id,
        otp_code: otpInput.trim(),
        preference: needsPreference ? preference : null,
      });
      setResult({ duplicate: false, ...response.data });
      setStep("result");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        setResult({ duplicate: true, ...(detail.checkin || {}) });
        setStep("result");
      } else {
        setError(typeof detail === "string" ? detail : "Could not verify the OTP. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="checkin-page">
      <div className="topbar">
        <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
        <button className="text-button" onClick={onOpenLog} data-testid="open-monthly-log-button"><FileText size={16} /> Monthly log</button>
      </div>
      <div className="checkin-layout">
        <section className="intro">
          <div className="eyebrow"><span className="live-dot" /> CAFETERIA CHECK-IN</div>
          <img className="baron-logo" src="https://customer-assets-lxgj4vgw.emergentagent.net/job_meal-tracker-app-20/artifacts/kfxr9btx_baron%20final%20Logo.jpg" alt="Baron Kitchen" data-testid="baron-kitchen-logo" />
          <p className="intro-copy">A quick check-in keeps today’s meal service running smoothly.</p>
          <div className="date-line" data-testid="current-date"><CalendarDays size={17} /> {prettyDate}</div>
          <div className="meal-strip" data-testid="meal-schedule">
            {mealPills.map((meal) => (
              <div className={`meal-pill ${meal.tone}`} key={meal.name}>
                <span>{meal.name}</span><small>{meal.time}</small>
              </div>
            ))}
          </div>
        </section>
        <section className="form-panel">
          <div className="panel-heading">
            <div>
              <p className="section-kicker">TODAY’S SERVICE</p>
              <h2>{step === "otp" ? "Verify OTP" : step === "result" ? (result?.is_beyond_hours ? "Logged outside hours" : result?.duplicate ? "Already recorded" : "Check-in confirmed") : "Mark your meal"}</h2>
            </div>
            <span className="service-icon">{step === "otp" ? <KeyRound size={20} /> : <Utensils size={20} />}</span>
          </div>

          {step === "form" && (
            <form onSubmit={startCheckin} data-testid="check-in-form">
              <label>FULL NAME
                <input data-testid="employee-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" />
              </label>
              <label>MOBILE NUMBER
                <input data-testid="employee-mobile-input" required type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="e.g. 98765 43210" />
              </label>
              <label>EMPLOYEE CODE
                <input data-testid="employee-code-input" required value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="e.g. NP-2048" />
              </label>
              {error && <div className="error-message" data-testid="check-in-error"><X size={16} />{error}</div>}
              <button className="submit-button" type="submit" disabled={submitting} data-testid="submit-check-in-button">
                {submitting ? "Please wait…" : "Get OTP"} <ChevronRight size={18} />
              </button>
              <p className="privacy-note">Your details are used only for cafeteria billing records.</p>
            </form>
          )}

          {step === "otp" && session && (
            <form onSubmit={confirmOtp} className="otp-step" data-testid="otp-step">
              <p className="otp-hint">Enter the one-time code below to complete your check-in.</p>
              <div className="otp-display" data-testid="otp-display">{session.otp_code}</div>
              {needsPreference && showChoice && (
                <div className="pref-group" data-testid="preference-group">
                  <p className="pref-label">PICK YOUR {session.meal_type.toUpperCase()} PREFERENCE</p>
                  <div className="pref-options">
                    <button
                      type="button"
                      className={`pref-chip veg ${preference === "Veg" ? "is-active" : ""}`}
                      onClick={() => setPreference("Veg")}
                      data-testid="preference-veg"
                    >
                      <span className="pref-dot" /> Veg
                    </button>
                    <button
                      type="button"
                      className={`pref-chip nonveg ${preference === "Non-Veg" ? "is-active" : ""}`}
                      onClick={() => setPreference("Non-Veg")}
                      data-testid="preference-nonveg"
                    >
                      <span className="pref-dot" /> Non-Veg
                    </button>
                  </div>
                </div>
              )}
              {needsPreference && !showChoice && (
                <div className={`pref-locked ${allowedPrefs[0] === "Veg" ? "veg" : "nonveg"}`} data-testid="preference-locked">
                  <span className="pref-dot" />
                  <div>
                    <strong>{allowedPrefs[0]} only</strong>
                    <small>Today's {session.meal_type.toLowerCase()} is {allowedPrefs[0].toLowerCase()}.</small>
                  </div>
                </div>
              )}
              <label>ENTER OTP
                <input
                  data-testid="otp-input"
                  required
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  className="otp-input"
                />
              </label>
              {error && <div className="error-message" data-testid="otp-error"><X size={16} />{error}</div>}
              <button className="submit-button" type="submit" disabled={submitting || otpInput.length !== 6 || (needsPreference && !preference)} data-testid="confirm-otp-button">
                {submitting ? "Verifying…" : "Verify & check-in"} <ChevronRight size={18} />
              </button>
              <button type="button" className="secondary-button" onClick={resetAll} data-testid="otp-cancel-button">Cancel</button>
            </form>
          )}

          {step === "result" && result && (
            <div className={`result-state ${result.duplicate ? "is-duplicate" : ""} ${result.is_beyond_hours ? "is-beyond" : ""}`} data-testid="check-in-result">
              <div className="result-icon">
                {result.is_beyond_hours ? <ShieldAlert size={30} /> : result.duplicate ? <Clock3 size={30} /> : <Check size={31} />}
              </div>
              <p className="result-kicker">
                {result.is_beyond_hours ? "BEYOND SERVICE HOURS" : result.duplicate ? "ALREADY RECORDED" : "YOU’RE ALL SET"}
              </p>
              <h3 data-testid="result-meal-type">
                {result.is_beyond_hours ? "No Food Service Available" : result.meal_type}
              </h3>
              {result.preference && !result.is_beyond_hours && (
                <div className={`result-pref ${result.preference === "Veg" ? "veg" : "nonveg"}`} data-testid="result-preference">
                  <span className="pref-dot" /> {result.preference}
                </div>
              )}
              <p>
                {result.is_beyond_hours
                  ? `Your visit has been noted, ${result.name}, but no meal is being served at this time.`
                  : result.duplicate
                    ? `${result.name} was already checked in for this meal today.`
                    : `Welcome, ${result.name}. Your meal has been logged.`}
              </p>
              <div className="result-date-big" data-testid="result-service-date">{formatServiceDate(result.service_date)}</div>
              <div className="result-meta">
                <span>{new Date(result.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <button className="secondary-button" onClick={resetAll} data-testid="new-check-in-button">
                New check-in <ChevronRight size={16} />
              </button>
            </div>
          )}
        </section>
      </div>
      <div className="landing-footer">
        <button type="button" className="admin-link-button" onClick={onOpenAdmin} data-testid="open-admin-button">
          <UserCog size={16} /> Admin view
        </button>
      </div>
    </main>
  );
}

function LogView({ onBack }) {
  const [filterMode, setFilterMode] = useState("month"); // "month" | "range"
  const [month, setMonth] = useState(monthValue);
  const [startDate, setStartDate] = useState(monthStartISO);
  const [endDate, setEndDate] = useState(todayISO);
  const [search, setSearch] = useState("");
  const [preferenceFilter, setPreferenceFilter] = useState("all"); // "all" | "Veg" | "Non-Veg"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const queryParams = useMemo(() => {
    if (filterMode === "range") return { start_date: startDate, end_date: endDate, search };
    return { month, search };
  }, [filterMode, month, startDate, endDate, search]);

  const exportHref = useMemo(() => {
    const params = new URLSearchParams(
      filterMode === "range"
        ? { start_date: startDate, end_date: endDate }
        : { month }
    );
    return `${API}/checkins/export?${params.toString()}`;
  }, [filterMode, month, startDate, endDate]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/checkins`, { params: queryParams })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [queryParams]);

  const counts = useMemo(
    () => DEFAULT_MEALS.reduce((acc, m) => ({ ...acc, [m.name]: rows.filter((r) => r.meal_type === m.name).length }), {}),
    [rows]
  );
  const splits = useMemo(() => {
    const result = {};
    for (const m of DEFAULT_MEALS) {
      if (!MEALS_WITH_PREFERENCE.has(m.name)) continue;
      const mealRows = rows.filter((r) => r.meal_type === m.name);
      result[m.name] = {
        veg: mealRows.filter((r) => r.preference === "Veg").length,
        nonveg: mealRows.filter((r) => r.preference === "Non-Veg").length,
      };
    }
    return result;
  }, [rows]);
  const beyondCount = useMemo(() => rows.filter((r) => r.meal_type === "Beyond Hours" || r.is_beyond_hours).length, [rows]);

  const visibleRows = useMemo(() => {
    if (preferenceFilter === "all") return rows;
    return rows.filter((r) => r.preference === preferenceFilter);
  }, [rows, preferenceFilter]);

  const applyToday = () => {
    setFilterMode("range");
    setStartDate(todayISO);
    setEndDate(todayISO);
  };

  return (
    <main className="log-page">
      <div className="topbar">
        <button className="back-button" onClick={onBack} data-testid="back-to-checkin-button">← Check-in</button>
        <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
        <a className="download-button" href={exportHref} data-testid="download-log-button"><Download size={16} /> Download CSV</a>
      </div>
      <div className="log-content">
        <div className="log-heading">
          <div>
            <p className="eyebrow">OPERATIONS / MONTHLY LOG</p>
            <h1>Meal service,<br /><em>accounted for.</em></h1>
          </div>
          <p className="log-description">A clean record of every meal served, ready for your monthly company submission.</p>
        </div>

        <div className="filter-tabs" data-testid="filter-mode-tabs">
          <button type="button" className={`filter-tab ${filterMode === "month" ? "is-active" : ""}`} onClick={() => setFilterMode("month")} data-testid="filter-mode-month">By month</button>
          <button type="button" className={`filter-tab ${filterMode === "range" ? "is-active" : ""}`} onClick={() => setFilterMode("range")} data-testid="filter-mode-range">Date range</button>
          <button type="button" className="filter-tab subtle" onClick={applyToday} data-testid="filter-today-shortcut">Today</button>
        </div>

        <div className="log-controls">
          {filterMode === "month" ? (
            <label className="month-control">
              <CalendarDays size={17} />
              <input data-testid="log-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
          ) : (
            <>
              <label className="month-control" title="From date">
                <CalendarDays size={17} />
                <input data-testid="log-start-date-input" type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <span className="range-sep">to</span>
              <label className="month-control" title="To date">
                <CalendarDays size={17} />
                <input data-testid="log-end-date-input" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
            </>
          )}
          <label className="search-control">
            <Search size={17} />
            <input data-testid="log-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or employee code" />
          </label>
          <label className="pref-filter" title="Filter by preference">
            <Filter size={16} />
            <select
              data-testid="log-preference-filter"
              value={preferenceFilter}
              onChange={(e) => setPreferenceFilter(e.target.value)}
            >
              <option value="all">All preferences</option>
              <option value="Veg">Veg only</option>
              <option value="Non-Veg">Non-Veg only</option>
            </select>
          </label>
        </div>

        <div className="stat-row">
          {DEFAULT_MEALS.map((m) => {
            const split = splits[m.name];
            return (
              <div className="stat" key={m.name} data-testid={`meal-count-${m.name.toLowerCase().replaceAll(" ", "-")}`}>
                <span>{m.name}</span>
                <strong>{counts[m.name]}</strong>
                {split && (
                  <small className="stat-split" data-testid={`meal-split-${m.name.toLowerCase()}`}>
                    <span className="split-veg">{split.veg} Veg</span>
                    <span className="split-sep">·</span>
                    <span className="split-nonveg">{split.nonveg} Non-Veg</span>
                  </small>
                )}
              </div>
            );
          })}
          <div className="stat" data-testid="meal-count-beyond-hours"><span>Beyond Hours</span><strong>{beyondCount}</strong></div>
          <div className="stat total"><span>Total meals</span><strong>{rows.length}</strong></div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Time</th><th>Employee</th><th>Code</th><th>Meal</th><th>Preference</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="empty-state">Loading the log…</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan="6" className="empty-state" data-testid="empty-log-state">
                  {rows.length === 0 ? "No meals recorded for this period." : `No ${preferenceFilter} entries in this period.`}
                </td></tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id} data-testid="log-row">
                    <td>{row.service_date}</td>
                    <td>{new Date(row.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                    <td><strong>{row.name}</strong><small>{row.mobile}</small></td>
                    <td>{row.employee_code}</td>
                    <td>
                      <span className={`meal-tag ${row.meal_type === "Beyond Hours" ? "beyond" : ""}`}>{row.meal_type}</span>
                    </td>
                    <td>
                      {row.preference ? (
                        <span className={`pref-tag ${row.preference === "Veg" ? "veg" : "nonveg"}`} data-testid="log-preference-tag">
                          <span className="pref-dot" /> {row.preference}
                        </span>
                      ) : (
                        <span className="pref-dash" data-testid="log-preference-tag">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

function LogGate({ onBack, children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = (event) => {
    event.preventDefault();
    if (password === LOG_PASSWORD) {
      setUnlocked(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
    }
  };

  if (unlocked) return children;

  return (
    <main className="log-page">
      <div className="topbar">
        <button className="back-button" onClick={onBack} data-testid="back-to-checkin-button">← Check-in</button>
        <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
        <span style={{ width: 1 }} />
      </div>
      <div className="log-gate" data-testid="log-password-gate">
        <div className="log-gate-card">
          <span className="log-gate-icon"><Lock size={22} /></span>
          <p className="eyebrow">RESTRICTED ACCESS</p>
          <h2>Enter password to view the monthly log</h2>
          <p className="log-gate-copy">This record is for cafeteria operations only.</p>
          <form onSubmit={submit}>
            <label>PASSWORD
              <input
                data-testid="log-password-input"
                type="password"
                autoFocus
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter access password"
              />
            </label>
            {error && <div className="error-message" data-testid="log-password-error"><X size={16} />{error}</div>}
            <button className="submit-button" type="submit" data-testid="log-password-submit">
              Unlock log <ChevronRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

function AdminGate({ onBack, onAuthed }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { username: username.trim(), password });
      onAuthed(r.data.session_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="log-page">
      <div className="topbar">
        <button className="back-button" onClick={onBack} data-testid="admin-back-button">← Check-in</button>
        <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
        <span style={{ width: 1 }} />
      </div>
      <div className="log-gate" data-testid="admin-login-gate">
        <div className="log-gate-card">
          <span className="log-gate-icon"><UserCog size={22} /></span>
          <p className="eyebrow">ADMIN ACCESS</p>
          <h2>Sign in to manage cafeteria settings</h2>
          <p className="log-gate-copy">Only cafeteria admins can change timings and meal preferences.</p>
          <form onSubmit={submit}>
            <label>LOGIN ID
              <input
                data-testid="admin-username-input"
                autoFocus required value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Admin ID"
              />
            </label>
            <label>PASSWORD
              <input
                data-testid="admin-password-input"
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </label>
            {error && <div className="error-message" data-testid="admin-login-error"><X size={16} />{error}</div>}
            <button className="submit-button" type="submit" disabled={submitting} data-testid="admin-login-submit">
              {submitting ? "Signing in…" : "Sign in"} <ChevronRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

function AdminDashboard({ onBack, token, onLogout }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadAudit = useMemo(() => async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit?limit=100`, authHeader);
      setAudit(r.data);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
    } finally {
      setAuditLoading(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authHeader)
      .then((r) => setSettings(r.data))
      .catch((e) => {
        if (e.response?.status === 401) { onLogout(); return; }
        setError("Could not load settings.");
      });
    loadAudit();
  }, [authHeader, onLogout, loadAudit]);

  const updateWindow = (meal, field, value) => {
    const parsed = parseTimeString(value);
    setSettings((s) => ({
      ...s,
      meal_windows: {
        ...s.meal_windows,
        [meal]: {
          ...s.meal_windows[meal],
          [`${field}_h`]: parsed.h,
          [`${field}_m`]: parsed.m,
        },
      },
    }));
  };

  const updateDayRule = (day, meal, value) => {
    setSettings((s) => ({
      ...s,
      day_rules: {
        ...s.day_rules,
        [day]: { ...s.day_rules[day], [meal]: value },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/admin/settings`, settings, authHeader);
      setSavedAt(new Date());
      loadAudit();
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save. Please check timings.");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (pwForm.new_password.length < 6) {
      setPwError("New password must be at least 6 characters.");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    setPwSubmitting(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, authHeader);
      setPwSuccess("Password changed. You'll stay signed in on this device; next sign-in will need the new password.");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      loadAudit();
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      const detail = e.response?.data?.detail;
      setPwError(typeof detail === "string" ? detail : "Could not change password.");
    } finally {
      setPwSubmitting(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, authHeader); } catch {}
    onLogout();
  };

  if (!settings) {
    return (
      <main className="log-page">
        <div className="topbar">
          <button className="back-button" onClick={onBack} data-testid="admin-back-button">← Check-in</button>
          <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
          <span style={{ width: 1 }} />
        </div>
        <div className="log-content"><p className="empty-state">Loading admin settings…</p></div>
      </main>
    );
  }

  return (
    <main className="log-page admin-page">
      <div className="topbar">
        <button className="back-button" onClick={onBack} data-testid="admin-back-button">← Check-in</button>
        <div className="brand"><span className="brand-mark"><Leaf size={19} /></span><span>GTPL</span></div>
        <button className="text-button" onClick={logout} data-testid="admin-logout-button">
          <LogOut size={16} /> Sign out
        </button>
      </div>
      <div className="log-content">
        <div className="log-heading">
          <div>
            <p className="eyebrow">OPERATIONS / ADMIN</p>
            <h1>Cafeteria<br /><em>settings.</em></h1>
          </div>
          <p className="log-description">Set meal windows and the Veg/Non-Veg menu for each day of the week.</p>
        </div>

        <section className="admin-card">
          <div className="admin-card-head">
            <Settings size={18} />
            <div>
              <h3>Meal timings</h3>
              <p>Times are in IST (24-hour format). End time must be after start time.</p>
            </div>
          </div>
          <div className="timings-grid">
            {DEFAULT_MEALS.map((m) => {
              const w = settings.meal_windows[m.name] || m.windowDefault;
              return (
                <div className={`timing-row ${m.tone}`} key={m.name} data-testid={`admin-timing-${m.name.toLowerCase().replaceAll(" ", "-")}`}>
                  <div className="timing-name">{m.name}</div>
                  <label className="timing-input">
                    <span>Start</span>
                    <input
                      data-testid={`admin-start-${m.name.toLowerCase().replaceAll(" ", "-")}`}
                      type="time" value={windowToTimeString(w, "start")}
                      onChange={(e) => updateWindow(m.name, "start", e.target.value)}
                    />
                  </label>
                  <label className="timing-input">
                    <span>End</span>
                    <input
                      data-testid={`admin-end-${m.name.toLowerCase().replaceAll(" ", "-")}`}
                      type="time" value={windowToTimeString(w, "end")}
                      onChange={(e) => updateWindow(m.name, "end", e.target.value)}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-card-head">
            <Utensils size={18} />
            <div>
              <h3>Day-wise Veg / Non-Veg menu</h3>
              <p>Choose what's served each day. "Both" lets employees pick; otherwise it's auto-set.</p>
            </div>
          </div>
          <div className="day-rules-table">
            <div className="day-rules-head">
              <div>Day</div>
              <div>Lunch</div>
              <div>Dinner</div>
            </div>
            {WEEKDAYS.map((d) => (
              <div className="day-rules-row" key={d.key} data-testid={`admin-day-${d.key}`}>
                <div className="day-rules-day">{d.label}</div>
                {["Lunch", "Dinner"].map((meal) => (
                  <div className="day-rules-cell" key={meal}>
                    {["Veg", "Non-Veg", "Both"].map((opt) => {
                      const current = settings.day_rules[d.key]?.[meal] || "Both";
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={`day-chip ${opt === "Veg" ? "veg" : opt === "Non-Veg" ? "nonveg" : "both"} ${current === opt ? "is-active" : ""}`}
                          onClick={() => updateDayRule(d.key, meal, opt)}
                          data-testid={`admin-rule-${d.key}-${meal.toLowerCase()}-${opt.toLowerCase().replace("-", "")}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {error && <div className="error-message admin-error" data-testid="admin-save-error"><X size={16} />{error}</div>}
        <div className="admin-actions">
          <button className="submit-button" onClick={save} disabled={saving} data-testid="admin-save-button">
            {saving ? "Saving…" : "Save changes"} <ChevronRight size={18} />
          </button>
          {savedAt && <span className="saved-pill" data-testid="admin-saved-indicator">Saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>

        <section className="admin-card">
          <div className="admin-card-head">
            <KeyRound size={18} />
            <div>
              <h3>Change admin password</h3>
              <p>Pick a new password at least 6 characters long. Your current password is required.</p>
            </div>
          </div>
          <form className="admin-password-form" onSubmit={changePassword} data-testid="admin-change-password-form">
            <label>CURRENT PASSWORD
              <input
                data-testid="admin-current-password"
                type="password" required value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                autoComplete="current-password"
              />
            </label>
            <label>NEW PASSWORD
              <input
                data-testid="admin-new-password"
                type="password" required minLength={6} value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                autoComplete="new-password"
              />
            </label>
            <label>CONFIRM NEW PASSWORD
              <input
                data-testid="admin-confirm-password"
                type="password" required value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                autoComplete="new-password"
              />
            </label>
            {pwError && <div className="error-message" data-testid="admin-password-error"><X size={16} />{pwError}</div>}
            {pwSuccess && <div className="success-message" data-testid="admin-password-success"><Check size={16} />{pwSuccess}</div>}
            <button className="submit-button" type="submit" disabled={pwSubmitting} data-testid="admin-change-password-submit">
              {pwSubmitting ? "Updating…" : "Update password"} <ChevronRight size={18} />
            </button>
          </form>
        </section>

        <section className="admin-card">
          <div className="admin-card-head">
            <History size={18} />
            <div>
              <h3>Change log</h3>
              <p>A record of every settings edit and password change, most recent first.</p>
            </div>
          </div>
          <div className="audit-list" data-testid="admin-audit-list">
            {auditLoading ? (
              <p className="empty-state">Loading change history…</p>
            ) : audit.length === 0 ? (
              <p className="empty-state" data-testid="admin-audit-empty">No changes recorded yet.</p>
            ) : (
              audit.map((entry) => (
                <div className="audit-entry" key={entry.id} data-testid="admin-audit-entry">
                  <div className="audit-head">
                    <span className={`audit-badge ${entry.action}`}>
                      {entry.action === "password_changed" ? "Password changed" : "Settings updated"}
                    </span>
                    <span className="audit-meta">
                      by <strong>{entry.actor}</strong> · {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </div>
                  {entry.action === "settings_updated" && entry.details?.changes?.length > 0 && (
                    <ul className="audit-changes">
                      {entry.details.changes.map((c, i) => (
                        <li key={i}>
                          {c.type === "timing" ? (
                            <><strong>{c.meal}</strong> timing <span className="audit-from">{c.from}</span> → <span className="audit-to">{c.to}</span></>
                          ) : (
                            <><strong>{c.day[0].toUpperCase() + c.day.slice(1)}</strong> {c.meal} <span className="audit-from">{c.from}</span> → <span className="audit-to">{c.to}</span></>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function App() {
  const path = window.location.pathname;
  const initialView = path === "/log" ? "log" : path === "/admin" ? "admin" : "checkin";
  const [view, setView] = useState(initialView);
  const [adminToken, setAdminToken] = useState(null);
  const goCheckin = () => { window.history.pushState({}, "", "/"); setView("checkin"); };
  const goLog = () => { window.history.pushState({}, "", "/log"); setView("log"); };
  const goAdmin = () => { window.history.pushState({}, "", "/admin"); setView("admin"); };

  return (
    <div className="App">
      {view === "log" && (
        <LogGate onBack={goCheckin}>
          <LogView onBack={goCheckin} />
        </LogGate>
      )}
      {view === "admin" && (
        adminToken ? (
          <AdminDashboard onBack={goCheckin} token={adminToken} onLogout={() => setAdminToken(null)} />
        ) : (
          <AdminGate onBack={goCheckin} onAuthed={(t) => setAdminToken(t)} />
        )
      )}
      {view === "checkin" && (
        <CheckIn onOpenLog={goLog} onOpenAdmin={goAdmin} />
      )}
    </div>
  );
}

export default App;
